import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Event } from './helpers';
import { TranslatableEditor } from '../../../Translations/Inputs';
import { getServerErrors } from '../../../../common/helpers';
import { replacementKeys } from './EditEventConfirmationMessagesForm';
import { translationsForAttribute } from '../../../Translations/helpers';
import MailEditorLayout from '../../../Common/MailEditorLayout';
import UI from '../../../../common/UI';
import useEditEventMutation from './useEditEventMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useValidation from '../../../../common/useValidation';

export interface EditEventPreregistrationFormProps {
  event: Event;
}

const EditEventPreregistrationForm = ({ event }: EditEventPreregistrationFormProps) => {
  const { t } = useTranslation();

  const getValues = (event: Event) => ({
    confirmation_mail_text: event.preregistration_mail_text,
    confirmation_mail_text_translations: translationsForAttribute(
      event.translations,
      'preregistration_mail_text',
    ),
  });

  const form = useForm({ values: getValues(event) });

  useNavigationPrompt(form.changed());

  const editEventPreregistrationSchema = yup.object().shape({});

  const [formKey, setFormKey] = useState(0);

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      preregistration: {
        ...form.values,
      },
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
      setFormKey((key) => key + 1);
    },
  });

  const { valid, errors } = useValidation({
    schema: editEventPreregistrationSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'preregistration'),
  });

  return (
    <UI.Form onSubmit={() => editEvent()} key={formKey}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />
        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('preregistration')}
          </UI.Legend>
          <UI.InputDescription>
            {t('preregistration_description')}
          </UI.InputDescription>
        </UI.Div>
        <UI.InputGroup sc={{ valid: !errors.confirmation_mail_text, touched: form.touched.confirmation_mail_text }}>
          <UI.InputLabel id="PreregistrationMailTextLabel">
            {t('preregistration_mail_text')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('preregistration_mail_text_description')}
          </UI.InputDescription>
          <MailEditorLayout
            editor={(props) => (
              <TranslatableEditor
                form={form}
                errors={errors}
                name="confirmation_mail_text"
                title={t('preregistration_mail_text')}
                defaultLocale={event.locale}
                options="full"
                variables={replacementKeys}
                attributes={{ 'aria-labelledby': 'PreregistrationMailTextLabel' }}
                {...props}
              />
            )}
            value={form.values.confirmation_mail_text}
            event={event}
            showRegistrationSummary
          />
          <UI.ErrorMessages
            attribute={t('preregistration_mail_text')}
            errors={errors.confirmation_mail_text}
          />
        </UI.InputGroup>
        <UI.HR />
        <UI.FormButtons form={form} submitLabel={t('save_event')} loading={loading} valid={valid} />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditEventPreregistrationForm;
